import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Skeleton } from "@repo/ui/components/skeleton";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ArrowRight, CalendarDays, Gamepad2, Layers3 } from "lucide-react";
import { z } from "zod";

type MediaDayDay = {
  date: string;
  sets: number;
  games: number;
  teamsQueued: number;
  teamsDone: number;
  teamsEdited: number;
  teamsCropped: number;
  gameTitles: {
    id: string;
    name: string;
    logoUrl: string | null;
    color: string | null;
    sets: string[];
  }[];
};

const WEEK_RANGES = [
  { label: "Week 0.0", startDate: "2026-06-20", endDate: "2026-06-28" },
  { label: "Week 0", startDate: "2026-06-29", endDate: "2026-07-05" },
  { label: "Week 1", startDate: "2026-07-06", endDate: "2026-07-12" },
  { label: "Week 2", startDate: "2026-07-13", endDate: "2026-07-19" },
  { label: "Week 3", startDate: "2026-07-20", endDate: "2026-07-26" },
  { label: "Week 4", startDate: "2026-07-27", endDate: "2026-08-02" },
  { label: "Week 5", startDate: "2026-08-03", endDate: "2026-08-09" },
  { label: "Week 6", startDate: "2026-08-10", endDate: "2026-08-16" },
  { label: "Week 7", startDate: "2026-08-17", endDate: "2026-08-23" },
] as const;

const parseDateKey = (date: string) => new Date(`${date}T00:00:00`);
const formatDateRange = (start: string, end: string) => {
  const startDate = parseDateKey(start);
  const endDate = parseDateKey(end);
  const startLabel = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endLabel = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${startLabel} – ${endLabel}`;
};

export const Route = createFileRoute("/_authed/media-day")({
  head: () => ({ meta: [{ title: "Media Day" }] }),
  component: RouteComponent,
  validateSearch: z
    .object({ week: z.coerce.number().int().min(1).max(WEEK_RANGES.length).optional() })
    .passthrough(),
});

function RouteComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { week } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const days = useQuery(api.ipad.listMediaDayDays) as MediaDayDay[] | undefined;

  const handleWeekChange = React.useCallback(
    (nextWeek: number) => navigate({ search: { week: nextWeek } }),
    [navigate],
  );

  const handleOpenDay = React.useCallback(
    (date: string) => navigate({ to: "/media-day/$date", params: { date } }),
    [navigate],
  );

  if (pathname !== "/media-day") return <Outlet />;
  if (!days) return <OverviewSkeleton />;

  return (
    <MediaDayOverview
      days={days}
      weekParam={week}
      onWeekChange={handleWeekChange}
      onOpenDay={handleOpenDay}
    />
  );
}

function MediaDayOverview({
  days,
  weekParam,
  onWeekChange,
  onOpenDay,
}: {
  days: MediaDayDay[];
  weekParam?: number;
  onWeekChange: (week: number) => void;
  onOpenDay: (date: string) => void;
}) {
  const todayKey = new Date().toLocaleDateString("sv-SE");

  const selectedWeekIndex = weekParam
    ? Math.min(Math.max(weekParam - 1, 0), WEEK_RANGES.length - 1)
    : WEEK_RANGES.findIndex((week) => todayKey >= week.startDate && todayKey <= week.endDate) !== -1
      ? WEEK_RANGES.findIndex((week) => todayKey >= week.startDate && todayKey <= week.endDate)
      : WEEK_RANGES.length - 1;

  const selectedWeek = WEEK_RANGES[selectedWeekIndex] ?? WEEK_RANGES[0]!;
  const weekDays = days
    .filter((day) => day.date >= selectedWeek.startDate && day.date <= selectedWeek.endDate)
    .toSorted((a, b) => a.date.localeCompare(b.date));

  const totalSets = weekDays.reduce((sum, day) => sum + day.sets, 0);
  const totalGames = weekDays.reduce((sum, day) => sum + day.games, 0);
  const totalTeams = weekDays.reduce((sum, day) => sum + day.teamsQueued, 0);
  const totalDone = weekDays.reduce((sum, day) => sum + day.teamsDone, 0);

  return (
    <div className="min-h-full bg-background p-4 text-foreground md:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Media Day</h1>
          <p className="text-sm text-muted-foreground">
            Queue teams for editorial and cutout sets on each Media Day.
          </p>
        </header>

        <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border bg-background/80 sm:grid-cols-5 xl:grid-cols-9">
          {WEEK_RANGES.map((week, index) => {
            const isActive = index === selectedWeekIndex;
            const isCurrentWeek = todayKey >= week.startDate && todayKey <= week.endDate;
            const isPastWeek = week.endDate < todayKey;
            return (
              <button
                key={week.label}
                type="button"
                aria-pressed={isActive}
                className={cn(
                  "min-h-20 cursor-pointer overflow-hidden border-border px-4 py-3 text-center transition-colors",
                  "border-r border-b xl:border-b-0",
                  isActive
                    ? "bg-accent text-accent-foreground ring-1 ring-primary/50 ring-inset"
                    : "text-muted-foreground hover:bg-accent/70 hover:text-accent-foreground",
                  isPastWeek && !isActive && "opacity-45 hover:opacity-75",
                  isCurrentWeek && !isActive && "ring-1 ring-primary/25 ring-inset",
                )}
                onClick={() => onWeekChange(index + 1)}
              >
                <span className="block text-sm font-semibold md:text-base">{week.label}</span>
                <span className="mt-1 block text-xs text-muted-foreground md:text-sm">
                  {formatDateRange(week.startDate, week.endDate)}
                </span>
                {isCurrentWeek ? (
                  <span className="mt-2 inline-flex rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold tracking-wide text-primary-foreground uppercase">
                    Current week
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <WeeklySummary
          totalSets={totalSets}
          totalGames={totalGames}
          totalTeams={totalTeams}
          totalDone={totalDone}
          dayCount={weekDays.length}
        />

        <section aria-label="Media Day dates" className="space-y-1.5">
          {weekDays.length ? (
            weekDays.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                isToday={day.date === todayKey}
                isPast={day.date < todayKey}
                onOpen={() => onOpenDay(day.date)}
              />
            ))
          ) : (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No Media Days scheduled in {selectedWeek.label}.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function WeeklySummary({
  totalSets,
  totalGames,
  totalTeams,
  totalDone,
  dayCount,
}: {
  totalSets: number;
  totalGames: number;
  totalTeams: number;
  totalDone: number;
  dayCount: number;
}) {
  const metrics = [
    {
      label: "Media days",
      value: dayCount,
      icon: CalendarDays,
      tone: "bg-chart-1/15 text-chart-1",
    },
    {
      label: "Scheduled sets",
      value: totalSets,
      icon: Layers3,
      tone: "bg-chart-3/15 text-chart-3",
    },
    {
      label: "Game slots",
      value: totalGames,
      icon: Gamepad2,
      tone: "bg-chart-2/15 text-chart-2",
    },
    {
      label: "Teams queued",
      value: totalTeams,
      icon: ArrowRight,
      tone: "bg-emerald-500/15 text-emerald-600",
    },
    {
      label: "Teams done",
      value: totalDone,
      icon: ArrowRight,
      tone: "bg-blue-500/15 text-blue-600",
    },
  ];

  return (
    <div className="overflow-hidden rounded-lg border bg-card/80 shadow-sm">
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-5 lg:items-center">
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={cn(
              "flex items-center gap-3 border-border",
              index > 0 && "sm:border-l sm:pl-6",
            )}
          >
            <span
              className={cn("flex size-9 items-center justify-center rounded-full", metric.tone)}
            >
              <metric.icon className="size-5" aria-hidden="true" />
            </span>
            <div>
              <div className="text-lg font-semibold">{metric.value}</div>
              <div className="text-sm text-muted-foreground">{metric.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DayRow({
  day,
  isToday,
  isPast,
  onOpen,
}: {
  day: MediaDayDay;
  isToday: boolean;
  isPast: boolean;
  onOpen: () => void;
}) {
  const date = parseDateKey(day.date);
  const hasTeams = day.teamsQueued > 0;
  const donePct = day.teamsQueued > 0 ? Math.round((day.teamsDone / day.teamsQueued) * 100) : 0;
  const editedPct = day.teamsQueued > 0 ? Math.round((day.teamsEdited / day.teamsQueued) * 100) : 0;

  return (
    <button
      type="button"
      className={cn(
        "grid w-full cursor-pointer gap-4 rounded-lg border bg-card/70 p-4 text-left transition-colors hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:grid-cols-[130px_1fr_180px] lg:items-start",
        isToday ? "border-primary/60 ring-1 ring-primary/20 ring-inset" : "border-border",
        isPast && "opacity-55 hover:opacity-80",
      )}
      onClick={onOpen}
    >
      <DateCell date={date} isToday={isToday} />

      <div className="flex flex-col gap-4 lg:border-l lg:pl-5">
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{day.sets} sets</Badge>
          <Badge variant="outline">{day.games} games</Badge>
          {day.teamsQueued > 0 ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-600">
              {day.teamsQueued} teams
            </Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500/40 text-amber-600">
              No teams yet
            </Badge>
          )}
        </div>

        <GameTitleList games={day.gameTitles ?? []} />

        {hasTeams && (
          <div className="flex flex-col gap-2">
            <ProgressRow
              label="Shot"
              value={day.teamsDone}
              max={day.teamsQueued}
              pct={donePct}
              color="bg-emerald-500"
            />
            <ProgressRow
              label="Edited"
              value={day.teamsEdited}
              max={day.teamsQueued}
              pct={editedPct}
              color="bg-blue-500"
            />
            <ProgressRow
              label="Cropped"
              value={day.teamsCropped}
              max={day.teamsQueued}
              pct={day.teamsQueued > 0 ? Math.round((day.teamsCropped / day.teamsQueued) * 100) : 0}
              color="bg-violet-500"
            />
          </div>
        )}
      </div>

      <div className="flex justify-start lg:justify-end">
        <span className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground shadow-xs">
          Open day <ArrowRight className="size-4" />
        </span>
      </div>
    </button>
  );
}

function GameTitleList({ games }: { games: MediaDayDay["gameTitles"] }) {
  if (!games.length) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/20 p-3 text-sm text-muted-foreground">
        No scheduled game titles found for this date.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Games on this date
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {games.map((game) => (
          <div
            key={game.id}
            className="flex min-w-0 gap-3 rounded-lg border bg-background p-3"
            style={
              game.color
                ? {
                    borderLeftWidth: 4,
                    borderLeftColor: game.color,
                    borderColor: `${game.color}55`,
                  }
                : undefined
            }
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-card">
              <span
                className="size-3 rounded-full"
                style={{ backgroundColor: game.color ?? "hsl(var(--muted-foreground))" }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-semibold text-foreground">{game.name}</div>
              {game.sets.length ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {game.sets.map((set) => (
                    <span
                      key={set}
                      className="rounded border bg-muted/40 px-1.5 py-0.5 text-[11px] leading-4 text-muted-foreground"
                    >
                      {set}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressRow({
  label,
  value,
  max,
  pct,
  color,
}: {
  label: string;
  value: number;
  max: number;
  pct: number;
  color: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 text-xs text-muted-foreground">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
        {value}/{max}
      </span>
    </div>
  );
}

function DateCell({ date, isToday }: { date: Date; isToday: boolean }) {
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });

  return (
    <div className="w-20 text-center leading-none">
      <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {month}
      </div>
      <div className="mt-1 text-3xl font-semibold text-foreground">{date.getDate()}</div>
      <div className="mt-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {weekday}
      </div>
      {isToday ? (
        <div className="mt-2 text-xs font-semibold text-primary uppercase">Today</div>
      ) : null}
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="min-h-full bg-background p-4 md:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
        <Skeleton className="h-12 w-80" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-24 w-full" />
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-28 w-full" />
        ))}
      </div>
    </div>
  );
}
